import React, { useState, useMemo } from 'react';
import { Input } from '../../ui/input';
import { EmployeeRecord } from '@/utils/employee-indexeddb';
import {
  HEADQUARTER_FILTER_PREFIX,
  DEPARTMENT_FILTER_PREFIX,
} from '@/utils/department-utils';
import { DateRange } from '../../../constants/ui';
import { ExportRange, SortState } from '../types';
import { NO_USAGE_ALIAS } from '../constants';
import { UsageDetailsData } from '@/utils/openai-api/types';

const compactControlButtonClass =
  'flex h-8 items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors';
const compactControlLabelClass = 'text-xs text-gray-700 dark:text-gray-300';

type CustomDateRange = { startDate: Date; endDate: Date } | null;

type UpdateURL = (
  filter: string,
  sort: SortState,
  dateRange?: DateRange,
  customDateRange?: CustomDateRange,
  workspace?: string,
  model?: string,
) => void;

interface WorkspaceFilterState {
  selectedWorkspace: string;
  setSelectedWorkspace: (workspace: string) => void;
  showWorkspaceDropdown: boolean;
  setShowWorkspaceDropdown: (show: boolean) => void;
  availableWorkspaces: Array<{ id: string; name: string; provider?: 'openai' | 'anthropic' }>;
}

interface ModelFilterState {
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  showModelDropdown: boolean;
  setShowModelDropdown: (show: boolean) => void;
  availableModels: string[];
}

interface ApiKeyFilterState {
  filter: string;
  setFilter: (filter: string) => void;
}

interface ExcludedModelState {
  excludedModels: string[];
  setExcludedModels: (models: string[]) => void;
  showExcludeModelsDropdown: boolean;
  setShowExcludeModelsDropdown: (show: boolean) => void;
  allUsageModels: string[];
  saveExcludedModels: (models: string[]) => void;
}

interface EmployeeFilterState {
  employeeData: Map<string, EmployeeRecord>;
  selectedDepartment: string;
  setSelectedDepartment: (department: string) => void;
  selectedRole: string;
  setSelectedRole: (role: string) => void;
  employeeNameFilter: string;
  setEmployeeNameFilter: (name: string) => void;
  headquarterSelectValue: string;
  departmentSelectValue: string;
  activeHeadquarter: string;
  isDepartmentSelectionDisabled: boolean;
  normalizedDepartmentFilter: string;
  headquarterOptions: string[];
  departmentOptions: Map<string, string[]>;
}

interface ExportControlState {
  handleExportHtmlReport: (range: ExportRange) => void;
  pendingExportRange: ExportRange | null;
  data: UsageDetailsData | null;
}

interface URLSyncState {
  updateURL: UpdateURL;
  sort: SortState;
  dateRange: DateRange;
  customDateRange: CustomDateRange;
}

export interface FilterBarProps {
  workspaceFilter: WorkspaceFilterState;
  modelFilter: ModelFilterState;
  apiKeyFilter: ApiKeyFilterState;
  excludedModelFilter: ExcludedModelState;
  employeeFilter: EmployeeFilterState;
  exportControl: ExportControlState;
  urlSync: URLSyncState;
}

interface ApiKeyFilterSectionProps {
  workspaceFilter: WorkspaceFilterState;
  modelFilter: ModelFilterState;
  apiKeyFilter: ApiKeyFilterState;
  excludedModelFilter: ExcludedModelState;
  urlSync: URLSyncState;
}

const ApiKeyFilterSection: React.FC<ApiKeyFilterSectionProps> = ({
  workspaceFilter,
  modelFilter,
  apiKeyFilter,
  excludedModelFilter,
  urlSync,
}) => {
  const {
    selectedWorkspace,
    setSelectedWorkspace,
    showWorkspaceDropdown,
    setShowWorkspaceDropdown,
    availableWorkspaces,
  } = workspaceFilter;
  const {
    selectedModel,
    setSelectedModel,
    showModelDropdown,
    setShowModelDropdown,
    availableModels,
  } = modelFilter;
  const { filter, setFilter } = apiKeyFilter;
  const {
    excludedModels,
    setExcludedModels,
    showExcludeModelsDropdown,
    setShowExcludeModelsDropdown,
    allUsageModels,
    saveExcludedModels,
  } = excludedModelFilter;

  const [workspaceProviderFilter, setWorkspaceProviderFilter] = useState<'all' | 'openai' | 'anthropic'>('all');

  const hasBothProviders = useMemo(() => {
    const providers = new Set(availableWorkspaces.map(w => w.provider).filter(Boolean));
    return providers.has('openai') && providers.has('anthropic');
  }, [availableWorkspaces]);

  const filteredWorkspaces = useMemo(() => {
    if (workspaceProviderFilter !== 'all') {
      return availableWorkspaces.filter(w => w.provider === workspaceProviderFilter);
    }
    // Merge same-name workspaces across providers
    const byName = new Map<string, { ids: string[]; name: string; providers: Set<string> }>();
    for (const w of availableWorkspaces) {
      const existing = byName.get(w.name);
      if (existing) {
        existing.ids.push(w.id);
        if (w.provider) existing.providers.add(w.provider);
      } else {
        byName.set(w.name, { ids: [w.id], name: w.name, providers: new Set(w.provider ? [w.provider] : []) });
      }
    }
    return Array.from(byName.values()).map(g => ({
      // Use wsname: prefix for merged groups, plain id for singletons
      id: g.ids.length > 1 ? `wsname:${g.name}` : g.ids[0],
      name: g.name,
      provider: g.providers.size === 1 ? ([...g.providers][0] as 'openai' | 'anthropic') : g.providers.size > 1 ? ('mixed' as unknown) : undefined,
    }));
  }, [availableWorkspaces, workspaceProviderFilter]);

  const syncURL = (
    nextFilter: string,
    nextWorkspace: string,
    nextModel: string,
  ) => {
    urlSync.updateURL(
      nextFilter,
      urlSync.sort,
      urlSync.dateRange,
      urlSync.customDateRange,
      nextWorkspace,
      nextModel,
    );
  };

  const allModels = [...new Set([...allUsageModels, ...excludedModels])].sort();

  return (
    <div className="bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative workspace-dropdown">
          <button
            onClick={() => {
              const next = !showWorkspaceDropdown;
              setShowWorkspaceDropdown(next);
              if (next) {
                setShowModelDropdown(false);
                setShowExcludeModelsDropdown(false);
              }
            }}
            className={compactControlButtonClass}
          >
            <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className={`${compactControlLabelClass} max-w-[100px] sm:max-w-[180px] truncate`}>
              {selectedWorkspace === 'all'
                ? 'All Workspaces'
                : selectedWorkspace.startsWith('wsname:')
                  ? selectedWorkspace.slice(7)
                  : availableWorkspaces.find((workspace) => workspace.id === selectedWorkspace)?.name || 'All Workspaces'}
            </span>
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showWorkspaceDropdown && (
            <div className="absolute top-full left-0 mt-1 w-[calc(100vw-2rem)] sm:w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20">
              {hasBothProviders && (
                <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/60 rounded-lg p-0.5 border border-slate-200 dark:border-slate-700">
                    {(['all', 'openai', 'anthropic'] as const).map(source => {
                      const isActive = workspaceProviderFilter === source;
                      const label = source === 'all' ? 'All' : source === 'openai' ? 'OpenAI' : 'Anthropic';
                      return (
                        <button
                          key={source}
                          onClick={() => setWorkspaceProviderFilter(source)}
                          className={`flex-1 px-2 py-0.5 text-xs font-medium rounded-md transition-colors ${
                            isActive
                              ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow'
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="py-1 max-h-72 overflow-y-auto custom-scrollbar">
                <button
                  onClick={() => {
                    setSelectedWorkspace('all');
                    setShowWorkspaceDropdown(false);
                    syncURL(filter, 'all', selectedModel);
                  }}
                  className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedWorkspace === 'all' ? 'bg-gray-100 dark:bg-gray-700' : ''
                  }`}
                >
                  <span className="text-gray-900 dark:text-gray-100">All Workspaces</span>
                </button>
                {filteredWorkspaces.map((workspace) => (
                  <button
                    key={workspace.id}
                    onClick={() => {
                      setSelectedWorkspace(workspace.id);
                      setShowWorkspaceDropdown(false);
                      syncURL(filter, workspace.id, selectedModel);
                    }}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between gap-2 ${
                      selectedWorkspace === workspace.id ? 'bg-gray-100 dark:bg-gray-700' : ''
                    }`}
                  >
                    <span className="text-gray-900 dark:text-gray-100 truncate">{workspace.name}</span>
                    {workspace.provider && hasBothProviders && (
                      workspace.provider === 'mixed' ? (
                        <span className="shrink-0 flex gap-0.5">
                          <span className="px-1 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">O</span>
                          <span className="px-1 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">A</span>
                        </span>
                      ) : (
                        <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                          workspace.provider === 'anthropic'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {workspace.provider === 'anthropic' ? 'A' : 'O'}
                        </span>
                      )
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="relative model-dropdown">
          <button
            onClick={() => {
              const next = !showModelDropdown;
              setShowModelDropdown(next);
              if (next) {
                setShowWorkspaceDropdown(false);
                setShowExcludeModelsDropdown(false);
              }
            }}
            className={compactControlButtonClass}
          >
            <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className={`${compactControlLabelClass} max-w-[100px] sm:max-w-[220px] truncate`}>
              {selectedModel === 'all' ? 'All Models' : selectedModel}
            </span>
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showModelDropdown && (
            <div className="absolute top-full left-0 mt-1 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto custom-scrollbar">
              <div className="py-1">
                <button
                  onClick={() => {
                    setSelectedModel('all');
                    setShowModelDropdown(false);
                    syncURL(filter, selectedWorkspace, 'all');
                  }}
                  className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedModel === 'all' ? 'bg-gray-100 dark:bg-gray-700' : ''
                  }`}
                >
                  <span className="text-gray-900 dark:text-gray-100">All Models</span>
                </button>
                {availableModels.map((model) => (
                  <button
                    key={model}
                    onClick={() => {
                      setSelectedModel(model);
                      setShowModelDropdown(false);
                      syncURL(filter, selectedWorkspace, model);
                    }}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedModel === model ? 'bg-gray-100 dark:bg-gray-700' : ''
                    }`}
                  >
                    <span className="text-gray-900 dark:text-gray-100">{model}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <Input
          type="text"
          placeholder="Filter by API key name (supports regex)"
          value={filter}
          onChange={(event) => {
            const nextFilter = event.target.value;
            setFilter(nextFilter);
            syncURL(nextFilter, selectedWorkspace, selectedModel);
          }}
          className="w-full sm:w-64 !h-8 !text-xs"
        />

        <div className="relative exclude-models-dropdown">
          <button
            onClick={() => {
              const next = !showExcludeModelsDropdown;
              setShowExcludeModelsDropdown(next);
              if (next) {
                setShowWorkspaceDropdown(false);
                setShowModelDropdown(false);
              }
            }}
            className={compactControlButtonClass}
          >
            <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <span className={`${compactControlLabelClass} max-w-[100px] sm:max-w-[220px] truncate`}>
              Exclude Models ({excludedModels.length})
            </span>
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showExcludeModelsDropdown && (
            <div className="absolute top-full left-0 mt-1 w-[calc(100vw-2rem)] sm:w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20">
              <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Exclude Models from Statistics</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Selected models will be hidden from all statistics, charts, and calculations
                </p>
              </div>
              <div className="max-h-80 overflow-y-auto custom-scrollbar p-2">
                {allModels.map((modelName) => (
                  <label
                    key={modelName}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={excludedModels.includes(modelName)}
                      onChange={(event) => {
                        const newExcluded = event.target.checked
                          ? [...excludedModels, modelName]
                          : excludedModels.filter((model) => model !== modelName);
                        setExcludedModels(newExcluded);
                        saveExcludedModels(newExcluded);
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {modelName}
                      {!allUsageModels.includes(modelName) && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(not in current data)</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <div className="p-3 border-t border-gray-200 dark:border-gray-600 flex justify-between">
                <button
                  onClick={() => {
                    setExcludedModels([]);
                    saveExcludedModels([]);
                  }}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowExcludeModelsDropdown(false)}
                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface EmployeeFilterSectionProps {
  employeeFilter: EmployeeFilterState;
}

const EmployeeFilterSection: React.FC<EmployeeFilterSectionProps> = ({ employeeFilter }) => {
  if (employeeFilter.employeeData.size === 0) {
    return null;
  }

  const {
    selectedDepartment,
    setSelectedDepartment,
    selectedRole,
    setSelectedRole,
    employeeNameFilter,
    setEmployeeNameFilter,
    headquarterSelectValue,
    departmentSelectValue,
    activeHeadquarter,
    isDepartmentSelectionDisabled,
    normalizedDepartmentFilter,
    headquarterOptions,
    departmentOptions,
  } = employeeFilter;

  return (
    <div className="bg-white dark:bg-gray-800 p-2.5 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700 mt-4">
      <div className="flex items-start gap-2 sm:gap-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Headquarter</label>
            <div className="relative">
              <select
                value={headquarterSelectValue}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === 'all') {
                    setSelectedDepartment('all');
                  } else if (value === 'no-mapping') {
                    setSelectedDepartment('no-mapping');
                  } else if (value === 'no-usage') {
                    setSelectedDepartment('no-usage');
                  } else if (value.startsWith(HEADQUARTER_FILTER_PREFIX)) {
                    setSelectedDepartment(value);
                  } else {
                    setSelectedDepartment('all');
                  }
                }}
                className="px-2 py-1 pr-7 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-44"
              >
                <option value="all">All Headquarters</option>
                <option value="no-mapping">— No Mapping —</option>
                <option value="no-usage">{NO_USAGE_ALIAS} (No Usage)</option>
                {headquarterOptions.map((headquarter) => (
                  <option key={headquarter} value={`${HEADQUARTER_FILTER_PREFIX}${headquarter}`}>
                    {headquarter}
                  </option>
                ))}
              </select>
              {normalizedDepartmentFilter !== 'all' && (
                <button
                  onClick={() => setSelectedDepartment('all')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  title="Clear"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Department</label>
            <select
              value={departmentSelectValue}
              onChange={(event) => {
                const value = event.target.value;
                if (!activeHeadquarter) {
                  setSelectedDepartment('all');
                  return;
                }

                if (value === 'all') {
                  setSelectedDepartment('all');
                  return;
                }

                if (value === `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}`) {
                  setSelectedDepartment(value);
                  return;
                }

                if (value.startsWith(DEPARTMENT_FILTER_PREFIX)) {
                  setSelectedDepartment(value);
                }
              }}
              disabled={isDepartmentSelectionDisabled}
              className="px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-44"
            >
              <option value={activeHeadquarter ? `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}` : 'all'}>
                {activeHeadquarter ? 'All Departments' : 'Select headquarter first'}
              </option>
              {activeHeadquarter &&
                departmentOptions.get(activeHeadquarter)?.map((department) => (
                  <option
                    key={department}
                    value={`${DEPARTMENT_FILTER_PREFIX}${activeHeadquarter}::${department}`}
                  >
                    {department}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Role Search</label>
            <input
              type="text"
              value={selectedRole}
              onChange={(event) => setSelectedRole(event.target.value)}
              placeholder="Filter by role (regex supported)"
              title="Filter by role (regex supported)"
              className="flex h-7 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-white ring-offset-background file:border-0 file:bg-transparent file:text-xs file:font-medium placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full sm:w-40"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Name Search</label>
            <Input
              type="text"
              placeholder="Search employee..."
              value={employeeNameFilter}
              onChange={(event) => setEmployeeNameFilter(event.target.value)}
              className="w-full sm:w-48 h-[34px]"
              title="Filter by employee name (regex supported)"
            />
          </div>

          {(selectedDepartment !== 'all' || selectedRole !== '' || employeeNameFilter !== '') && (
            <button
              onClick={() => {
                setSelectedDepartment('all');
                setSelectedRole('');
                setEmployeeNameFilter('');
              }}
              className="self-end px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface ExportActionSectionProps {
  exportControl: ExportControlState;
}

const ExportActionSection: React.FC<ExportActionSectionProps> = ({ exportControl }) => {
  const { handleExportHtmlReport, pendingExportRange, data } = exportControl;
  const isDisabled = !data || !data.apiKeyUsage || pendingExportRange !== null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      <button
        onClick={() => handleExportHtmlReport('1day')}
        disabled={isDisabled}
        className="px-3 py-1.5 text-sm bg-purple-600 dark:bg-purple-700 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        title="Export HTML report for Today"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Export Today
      </button>
      <button
        onClick={() => handleExportHtmlReport('7days')}
        disabled={isDisabled}
        className="px-3 py-1.5 text-sm bg-purple-600 dark:bg-purple-700 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        title="Export HTML report for the last 7 days"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Export 7d
      </button>
      <button
        onClick={() => handleExportHtmlReport('30days')}
        disabled={isDisabled}
        className="px-3 py-1.5 text-sm bg-purple-600 dark:bg-purple-700 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        title="Export HTML report for the last 30 days"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Export 30d
      </button>
    </div>
  );
};

export const FilterBar: React.FC<FilterBarProps> = ({
  workspaceFilter,
  modelFilter,
  apiKeyFilter,
  excludedModelFilter,
  employeeFilter,
  exportControl,
  urlSync,
}) => {
  return (
    <>
      <ApiKeyFilterSection
        workspaceFilter={workspaceFilter}
        modelFilter={modelFilter}
        apiKeyFilter={apiKeyFilter}
        excludedModelFilter={excludedModelFilter}
        urlSync={urlSync}
      />
      <EmployeeFilterSection employeeFilter={employeeFilter} />
      <ExportActionSection exportControl={exportControl} />
    </>
  );
};
